import React, { useState, useEffect } from 'react';
import Header from './Header';
import PlaqueDimensionsForm from './PlaqueDimensionsForm';
import MurForm from './MurForm';
import MurVisualisation from './MurVisualisation';
import MurVisualisation3D from './MurVisualisation3D';
import PieceVisualisation3D from './PieceVisualisation3D';
import RoomCreationModal from './RoomCreationModal';
import ToggleChutes from './ToggleChutes';
import ResultatsGlobaux from './ResultatsGlobaux';
import ProjectManager from './ProjectManager';
import CostCalculator from './CostCalculator';
import HelpModal from './HelpModal';
import { Calculator, Download, X, Box, Square, LayoutGrid, Undo2, Redo2 } from 'lucide-react';
import '../styles/main.css';
import '../styles/components.css';
import { exporterResultatsEnPDF } from '../utils/pdfExport';
import PdfPreviewModal from './PdfPreviewModal';
import '../styles/pdfExport.css';
import useProjetStore from '../hooks/useProjetStore';
import useOptimisation from '../hooks/useOptimisation';

function App() {
  // --- Hooks personnalisés ---
  const {
    state: projetState,
    actions: projetActions,
    loadProject: loadProjet,
    canUndo,
    canRedo
  } = useProjetStore();

  const {
    state: optiState,
    actions: optiActions
  } = useOptimisation(projetState.murs, projetState.plaqueDimensions);

  // --- États UI locaux ---
  const [darkMode, setDarkMode] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Modes de vue: '2d-mur', '3d-mur', '3d-global'
  const [viewMode, setViewMode] = useState('2d-mur');
  const [showRoomModal, setShowRoomModal] = useState(false);

  // États pour le choix du type d'ouverture
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [newOuverturePosition, setNewOuverturePosition] = useState({ x: 30, y: 30 });

  // --- Effets ---
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Raccourcis clavier pour undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          projetActions.redo();
        } else {
          projetActions.undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        projetActions.redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [projetActions]);

  // --- Handlers UI ---
  const toggleDarkMode = () => setDarkMode(!darkMode);

  const openOuvertureSelector = () => {
    const murActuel = projetState.murSelectionnee;

    let posX = 30;
    let posY = 30;

    if (murActuel.ouvertures.length > 0) {
      const derniere = murActuel.ouvertures[murActuel.ouvertures.length - 1];
      posX = Math.min(murActuel.largeur - 100, derniere.x + 120);

      if (posX + 90 > murActuel.largeur) {
        posX = 30;
        posY = Math.min(murActuel.hauteur - 100, derniere.y + 120);
      }
    }

    setNewOuverturePosition({ x: posX, y: posY });
    setShowTypeSelector(true);
  };

  const handleCreateOuverture = (type) => {
    projetActions.ajouterOuverture(type, newOuverturePosition);
    setShowTypeSelector(false);
  };

  const handleExportPdf = async () => {
    try {
      setShowPdfPreview(false);

      const loadingMessage = document.createElement('div');
      loadingMessage.className = 'loading-message';
      loadingMessage.textContent = 'Génération du PDF en cours...';
      document.body.appendChild(loadingMessage);

      const nomFichier = await exporterResultatsEnPDF(
        optiState.resultat,
        projetState.murs,
        projetState.plaqueDimensions
      );

      document.body.removeChild(loadingMessage);
      alert(`Export PDF réussi ! Fichier : ${nomFichier}`);
    } catch (error) {
      console.error("Erreur lors de l'export PDF :", error);
      alert("Une erreur est survenue lors de l'export PDF. Veuillez réessayer.");
    }
  };

  const demanderExportPDF = () => {
    if (!optiState.resultat) {
      alert("Veuillez d'abord calculer l'optimisation avant d'exporter en PDF");
      return;
    }
    setShowPdfPreview(true);
  };

  const lancerOptimisation = () => {
    optiActions.optimiserDecoupes(projetState.erreurs);
  };

  return (
    <div className={`app-container ${darkMode ? 'dark-mode' : ''}`}>
      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className="main-layout">
        <div className="left-panel">
          <PlaqueDimensionsForm
            dimensions={projetState.plaqueDimensions}
            onChange={projetActions.setPlaqueDimensions}
          />

          <MurForm
            murs={projetState.murs}
            murSelectionneeId={projetState.murSelectionneeId}
            onSelectMur={projetActions.setMurSelectionneeId}
            onModifierMur={projetActions.modifierMur}
            onAjouterMur={projetActions.ajouterMur}
            onSupprimerMur={projetActions.supprimerMur}
            ouvertureSelectionneeId={projetState.ouvertureSelectionneeId}
            onSelectOuverture={projetActions.setOuvertureSelectionneeId}
            onModifierOuverture={projetActions.modifierOuverture}
            onAjouterOuverture={openOuvertureSelector}
            onSupprimerOuverture={projetActions.supprimerOuverture}
            onCommitHistory={projetActions.commitToHistory}
            erreurs={projetState.erreurs}
          />

          <div className="action-buttons-container">
            {/* Undo/Redo buttons */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button
                className="btn btn-sm btn-secondary"
                onClick={projetActions.undo}
                disabled={!canUndo}
                title="Annuler (Ctrl+Z)"
                style={{ flex: 1, opacity: canUndo ? 1 : 0.5 }}
              >
                <Undo2 size={16} style={{ marginRight: '4px' }} />
                Annuler
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={projetActions.redo}
                disabled={!canRedo}
                title="Refaire (Ctrl+Y)"
                style={{ flex: 1, opacity: canRedo ? 1 : 0.5 }}
              >
                <Redo2 size={16} style={{ marginRight: '4px' }} />
                Refaire
              </button>
            </div>

            <ToggleChutes
              utiliserChutes={optiState.utiliserChutes}
              onToggle={optiActions.setUtiliserChutes}
            />

            <div className="action-buttons">
              <button
                className="btn btn-primary"
                onClick={() => setShowRoomModal(true)}
                title="Créer une pièce complète"
                style={{ marginBottom: '8px' }}
              >
                <LayoutGrid size={18} style={{ marginRight: '8px' }} />
                Créer une pièce
              </button>

              <button className="btn btn-success" onClick={lancerOptimisation}>
                <Calculator size={18} style={{ marginRight: '8px' }} />
                Calculer l'optimisation
              </button>

              <button className="btn btn-secondary btn-export-pdf" onClick={demanderExportPDF}>
                <Download size={18} className="icon" />
                Exporter en PDF
              </button>

              <CostCalculator
                resultat={optiState.resultat}
                dimensionsPlaque={projetState.plaqueDimensions}
              />

              <HelpModal />

              <ProjectManager
                murs={projetState.murs}
                plaqueDimensions={projetState.plaqueDimensions}
                onLoadProject={loadProjet}
              />
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="card">
            <div className="card-header">
              <h2>Visualisation</h2>
              <div className="tab-buttons">
                {projetState.murs.map(mur => (
                  <button
                    key={mur.id}
                    className={`btn btn-sm ${projetState.murSelectionneeId === mur.id ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => projetActions.setMurSelectionneeId(mur.id)}
                  >
                    {mur.nom}
                  </button>
                ))}
              </div>
              <div className="view-toggle" style={{ marginLeft: '10px' }}>
                <button
                  className={`btn btn-sm ${viewMode === '3d-global' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setViewMode('3d-global')}
                  title="Vue Globale de la Pièce"
                  style={{ marginRight: '5px' }}
                >
                  <LayoutGrid size={16} style={{ marginRight: '4px' }} />
                  Pièce 3D
                </button>
                <div style={{ width: '1px', height: '20px', backgroundColor: '#ccc', margin: '0 5px' }}></div>
                <button
                  className={`btn btn-sm ${viewMode === '3d-mur' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setViewMode('3d-mur')}
                  title="Vue 3D du Mur"
                  style={{ marginRight: '5px' }}
                >
                  <Box size={16} style={{ marginRight: '4px' }} />
                  Mur 3D
                </button>
                <button
                  className={`btn btn-sm ${viewMode === '2d-mur' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setViewMode('2d-mur')}
                  title="Vue 2D du Mur"
                >
                  <Square size={16} style={{ marginRight: '4px' }} />
                  Mur 2D
                </button>
              </div>
            </div>

            <div className="help-text">
              {viewMode === '3d-global'
                ? <p>Naviguez dans la pièce (Clic gauche: Rotation, Clic droit: Déplacement). Double-cliquez sur un mur pour l'éditer.</p>
                : <p>Cliquez et déplacez les ouvertures directement sur le plan du mur</p>
              }
            </div>

            {viewMode === '3d-global' ? (
              <PieceVisualisation3D
                murs={projetState.murs}
                resultat={optiState.resultat}
                murSelectionneeId={projetState.murSelectionneeId}
                onSelectMur={(id) => {
                  projetActions.setMurSelectionneeId(id);
                  // Optionnel: Passer en vue mur si on veut éditer tout de suite
                  // setViewMode('2d-mur');
                }}
              />
            ) : viewMode === '3d-mur' ? (
              <MurVisualisation3D
                mur={projetState.murSelectionnee}
                ossature={optiState.resultat?.murDetails?.find(m => m.murId === projetState.murSelectionneeId)?.ossature}
                resultat={optiState.resultat?.plaques}
              />
            ) : (
              <MurVisualisation
                mur={projetState.murSelectionnee}
                dimensionsPlaque={projetState.plaqueDimensions}
                echelle={0.5}
                onOuvertureSelect={projetActions.setOuvertureSelectionneeId}
                ouvertureSelectionneeId={projetState.ouvertureSelectionneeId}
                resultat={optiState.resultat?.plaques?.filter(p => p.murId === projetState.murSelectionneeId)}
                ossature={optiState.resultat?.murDetails?.find(m => m.murId === projetState.murSelectionneeId)?.ossature}
                onModifierOuverture={projetActions.modifierOuverture}
                onCommitHistory={projetActions.commitToHistory}
              />
            )}
          </div>

          {optiState.resultat && <ResultatsGlobaux resultat={optiState.resultat} murs={projetState.murs} />}
        </div>
      </div>

      <PdfPreviewModal
        isOpen={showPdfPreview}
        onClose={() => setShowPdfPreview(false)}
        resultat={optiState.resultat}
        murs={projetState.murs}
        dimensionsPlaque={projetState.plaqueDimensions}
        onExport={handleExportPdf}
      />

      {/* Modal de création de pièce */}
      <RoomCreationModal
        isOpen={showRoomModal}
        onClose={() => setShowRoomModal(false)}
        onCreate={(L, l, H, avecPlafond, type) => {
          if (type === 'L') {
            projetActions.creerPieceEnL(L, l, H, avecPlafond);
          } else {
            projetActions.creerPieceRectangulaire(L, l, H, avecPlafond);
          }
          setViewMode('3d-global'); // On bascule en vue globale après création
        }}
      />

      {/* Sélecteur de type d'ouverture */}
      {showTypeSelector && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ maxWidth: "400px", padding: "20px" }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Choisir le type d'ouverture</h3>
              <button className="btn-icon" onClick={() => setShowTypeSelector(false)}>
                <X size={20} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              <button className="btn btn-secondary" onClick={() => handleCreateOuverture('porte')}>Porte</button>
              <button className="btn btn-secondary" onClick={() => handleCreateOuverture('fenêtre')}>Fenêtre</button>
              <button className="btn btn-secondary" onClick={() => handleCreateOuverture('prise')}>Prise</button>
              <button className="btn btn-secondary" onClick={() => handleCreateOuverture('interrupteur')}>Interrupteur</button>
              <button className="btn btn-secondary" onClick={() => handleCreateOuverture('tuyau d\'eau')}>Tuyau d'eau</button>
              <button className="btn btn-secondary" onClick={() => handleCreateOuverture('autre')}>Autre</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;